import {
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { Navigate, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { AppShell } from "./components/AppShell";
import { MaintenanceScreen } from "./components/MaintenanceScreen";
import { PageError, PageLoading } from "./components/PageState";
import { QuadroToast } from "./components/kanban/QuadroToast";
import {
  api,
  ApiError,
  definirAoPerderSessao,
  ehSessaoAusente,
} from "./lib/api";
import { CHAVE_DA_MINHA_FICHA } from "./lib/api/ficha-da-equipe";
import {
  avisoDescompasso,
  lerEntradaEscolhida,
  limparEntradaEscolhida,
} from "./lib/entrada-login";
import { ROTA_DO_PERFIL } from "./components/app-nav";
import { useManutencao } from "./lib/maintenance";
import { demoPortalSession } from "./lib/portal-demo";
import type { Session } from "./lib/types";

// TUT-177: este arquivo deixou de ser o roteador e virou os PORTÕES — as
// decisões que não são rota (manutenção, harness demo, sessão, papel) e que
// antes viviam como `if`s antes do `<Routes>`. A árvore de rotas, agora um
// objeto que as guardas caminham, mora em `router.tsx`.

const LoginPage = lazy(() =>
  import("./pages/LoginPage").then((m) => ({ default: m.LoginPage })),
);
// Portal externo (TUT-080 fase 2c). Chunk próprio: nenhuma tela do escritório
// baixa este código. A bifurcação é por PAPEL, não por rota — ver o comentário
// no ponto da bifurcação, no TenantGate.
const PortalPage = lazy(() =>
  import("./pages/portal/PortalPage").then((m) => ({ default: m.PortalPage })),
);
// TUT-261 — a pagina inicial publica. Chunk PROPRIO, e isso e contrato, nao
// detalhe: nada do que ela carrega (a biblioteca de movimento inclusive) pode
// chegar a quem faz login. Ha trava contra isso em `motion-so-na-lp.test.ts`.
const LandingPage = lazy(() =>
  import("./pages/LandingPage").then((m) => ({ default: m.LandingPage })),
);

// Envolve um nó lazy numa fronteira de Suspense com o loading padrão.
function suspense(node: ReactNode) {
  return <Suspense fallback={<PageLoading />}>{node}</Suspense>;
}

/**
 * O portão da raiz: manutenção antes de qualquer rota.
 *
 * Modo manutenção (TUT-121): quando a queda da API foi confirmada, a tela de
 * manutenção substitui o app INTEIRO — inclusive login e console de
 * plataforma, que dependem da mesma API. Ela sonda /api/health e recarrega
 * sozinha quando o sistema volta.
 */
export function RootScreen() {
  const location = useLocation();
  // Cada rota representa um novo contexto operacional. Voltar ao topo evita
  // que uma rolagem profunda da tela anterior esconda o horizonte da próxima,
  // sobretudo no drawer móvel. Mudanças apenas de query preservam a posição —
  // e é por isso que a restauração de rolagem do próprio TanStack fica
  // desligada: ela restaura posição antiga no Voltar, que não é o contrato
  // que o app tinha.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  const manutencao = useManutencao();
  // Harness visual somente do adapter demo. A manutenção real continua vindo
  // exclusivamente do estado confirmado pela API; a query não existe em
  // builds de staging/produção e evita derrubar qualquer ambiente para provar
  // o estado global no CI.
  const manutencaoDemo =
    import.meta.env.VITE_API_MODE === "demo" &&
    new URLSearchParams(location.searchStr).get("__tutelae_maintenance") === "1";
  if (manutencao || manutencaoDemo) {
    return manutencaoDemo ? (
      <MaintenanceScreen onRecovered={() => undefined} />
    ) : (
      <MaintenanceScreen />
    );
  }
  return <Outlet />;
}

/**
 * Harness do portal externo (TUT-080 fase 2c), no precedente do harness de
 * manutenção acima: só existe no adapter demo, e serve para o e2e — que roda
 * sem API — abrir a tela do portal e passar axe/reflow nela antes de existir
 * usuário externo real. Em staging/produção a query não faz nada.
 *
 * O portão fica ACIMA das rotas públicas de identidade e do escritório, e
 * ABAIXO do Console — é a mesma precedência que o funil por `pathname` tinha.
 */
export function PortalDemoGate() {
  const location = useLocation();
  const navigate = useNavigate();
  const portalDemo =
    import.meta.env.VITE_API_MODE === "demo" &&
    ["1", "perito", "representante"].includes(
      new URLSearchParams(location.searchStr).get("__tutelae_portal") ?? "",
    );
  if (portalDemo)
    return suspense(
      <PortalPage
        session={demoPortalSession}
        onLogout={() => void navigate({ to: "/" })}
      />,
    );
  return <Outlet />;
}

/**
 * TUT-261 — o que uma pessoa SEM sessão vê, e isso depende de onde ela chegou.
 *
 * Até esta fatia a resposta era uma só: o formulário de login, renderizado no
 * lugar da rota. Agora `/` é a porta pública do produto — quem chega sem sessão
 * na raiz vê a **página inicial**, não um formulário. Em qualquer outra rota do
 * escritório o comportamento não muda, e não muda de propósito: o login
 * continua aparecendo NO LUGAR da rota pedida, sem redirecionar, para que
 * depois de autenticar a pessoa esteja onde queria estar. Essa era a decisão da
 * casa antes desta fatia e ela é melhor que redirecionar com parâmetro de
 * retorno — eu tinha planejado "acrescentar o retorno" e descobri, lendo, que
 * ele já existia numa forma superior.
 *
 * Quem quer o formulário de propósito tem endereço: `/entrar`, rota pública
 * irmã de `/senha/esqueci`. É para lá que o botão "Acessar o app" da página
 * inicial aponta.
 */
function semSessao(queryClient: QueryClient, pathname: string) {
  const autenticou = (value: Session) =>
    queryClient.setQueryData(["session"], value);
  if (pathname === "/") return suspense(<LandingPage />);
  return suspense(<LoginPage onAuthenticated={autenticou} />);
}

/**
 * O portão do escritório: sessão, login no lugar da rota, e a bifurcação por
 * papel. Só monta nas rotas do tenant — as rotas públicas de identidade e o
 * Console ficam fora dele na árvore, então a consulta de sessão nem existe
 * nelas (era o `enabled:` condicional do App antigo, agora é estrutura).
 */
export function TenantGate() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const session = useQuery({
    queryKey: ["session"],
    queryFn: api.session,
    retry: false,
    staleTime: 60_000,
  });
  // TUT-551 — a foto da ficha de quem está logado, para o menu da conta. Só
  // para a equipe interna (o externo não tem ficha); falhou, ficam as iniciais.
  const minhaFicha = useQuery({
    queryKey: CHAVE_DA_MINHA_FICHA,
    queryFn: () => api.minhaFicha(),
    enabled: Boolean(session.data) && session.data?.user.roleKey !== "EXTERNAL",
    retry: false,
    staleTime: 5 * 60_000,
  });

  // TUT-385 — sair do lado de cá SEMPRE, mesmo que o servidor não confirme.
  // NUNCA remover a query da sessão: `clear()` a removia e deixava o
  // observer deste portão ÓRFÃO — um `setQueryData` posterior cria outra
  // entrada no cache que ninguém observa, e a tela seguia logada com dados
  // velhos (medido no navegador: sessionStorage limpo, Dashboard na tela).
  // No App antigo o defeito não aparecia porque o componente inteiro
  // assinava useLocation e re-inscrevia o observer a cada navegação; aqui o
  // portão fica acima do Outlet e não re-renderiza à toa. Então: poda-se
  // tudo MENOS a sessão, e a sessão é NOTIFICADA — o espelho exato do
  // `onAuthenticated` do login.
  function encerrarSessaoLocal() {
    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] !== "session",
    });
    queryClient.setQueryData(["session"], null);
  }
  // Um 401 no meio do uso (sessão expirada, encerrada em outra aba ou pelo
  // admin) devolve ao login no lugar da rota — a URL fica, e depois de entrar
  // a pessoa está onde estava.
  useEffect(() => definirAoPerderSessao(encerrarSessaoLocal));

  // TUT-308 — o aviso de descompasso ("escolheu advogado, a conta é da
  // equipe"). Só resolve aqui o caso INTERNO: para EXTERNAL falta a
  // profissão do vínculo (COUNSEL × EXPERT), que só o `PortalPage` sabe (a
  // consulta do overview) — por isso ele NÃO limpa `sessionStorage` quando o
  // destino é EXTERNAL, e é o `PortalPage` que lê e limpa nesse caso.
  const [aviso, setAviso] = useState<{ id: number; message: string } | null>(
    null,
  );
  const avisoLido = useRef(false);
  useEffect(() => {
    if (!session.data || avisoLido.current) return;
    avisoLido.current = true;
    if (session.data.user.roleKey === "EXTERNAL") return;
    const entrada = lerEntradaEscolhida();
    limparEntradaEscolhida();
    const texto = avisoDescompasso(entrada, {
      external: false,
      profession: null,
    });
    if (texto) setAviso({ id: Date.now(), message: texto });
  }, [session.data]);
  useEffect(() => {
    if (!aviso) return;
    const timer = window.setTimeout(() => setAviso(null), 6000);
    return () => window.clearTimeout(timer);
  }, [aviso]);

  if (session.isLoading)
    return <PageLoading label="Preparando seu ambiente seguro..." />;
  if (session.isError) {
    // TUT-406 — só o 401 é "sem sessão". Antes, a queda de REDE (o `fetch`
    // lança `TypeError`, não `ApiError`) também caía no login: a pessoa, logada
    // e com a conexão oscilando, via a tela de entrar e achava que tinha saído.
    if (!ehSessaoAusente(session.error)) {
      return (
        <PageError
          message={
            session.error instanceof ApiError
              ? session.error.message
              : "Não foi possível falar com o servidor. Confira a conexão e tente de novo."
          }
          onRetry={() => void session.refetch()}
        />
      );
    }
    // Login renderizado NO LUGAR da rota, sem redirecionar: a URL pedida
    // permanece, e depois de autenticar a pessoa está onde queria estar.
    return semSessao(queryClient, location.pathname);
  }
  if (!session.data) return semSessao(queryClient, location.pathname);

  async function logout() {
    // TUT-385: o "Sair" não pode depender da resposta. Com a sessão já caída
    // (401), ou negada ao portal (o 403 que a TUT-385 consertou no servidor),
    // `await api.logout()` lançava, nada abaixo rodava e o botão não fazia
    // nada. O servidor é avisado quando dá; o lado de cá sai sempre.
    try {
      await api.logout();
    } catch {
      // Sem ação: a sessão local é encerrada logo abaixo de qualquer jeito.
    } finally {
      encerrarSessaoLocal();
      void navigate({ to: "/" });
    }
  }

  // TUT-080 fase 2c — a bifurcação por PAPEL, antes de qualquer rota do
  // escritório. Sessão EXTERNAL nunca renderiza o AppShell: um advogado de
  // fora digitando /processos vê o portal, porque a rota nem chega a ser
  // resolvida. É o espelho da fronteira do servidor (fase 2a): lá o RolesGuard
  // nega tudo que não é @Portal(); aqui a árvore do escritório nem monta —
  // esconder por CSS ou por item de menu seria fronteira de aparência.
  if (session.data.user.roleKey === "EXTERNAL")
    return suspense(
      <PortalPage session={session.data} onLogout={() => void logout()} />,
    );

  // TUT-544 — a pessoa interna sem grupo só alcança o Meu perfil, onde está o
  // aviso. O redirecionamento é do portão (e não de cada rota) porque vale para
  // TODA rota do escritório, a raiz inclusive; o servidor já nega o resto.
  if (session.data.semGrupo && location.pathname !== ROTA_DO_PERFIL)
    return <Navigate to={ROTA_DO_PERFIL} replace />;

  return (
    <>
      <AppShell session={session.data} fotoDaConta={minhaFicha.data?.photoUrl} onLogout={() => void logout()}>
        <Suspense fallback={<PageLoading />}>
          <Outlet />
        </Suspense>
      </AppShell>
      {aviso ? (
        <QuadroToast
          toast={{
            id: aviso.id,
            message: aviso.message,
            tone: "success",
            onUndo: null,
          }}
          onDismiss={() => setAviso(null)}
        />
      ) : null}
    </>
  );
}
